import { getBoundsShape } from '../plugins/poly/utils/shape';
import { point, type Point } from '../utils/math';

import { getElementBounds } from './bound';
import { type GenericElement } from './element';

export type GenericShape = { data: string; type: 'line' | 'area' };
export type LineSegment = [a: Point, b: Point];
export type Polyline = LineSegment[];

export const getSelectionBoxShape = (element: GenericElement) => {
  const coords = getElementBounds(element, { withRotation: false });
  return getBoundsShape(coords, element.x, element.y, element.angle);
};

export const rescalePoints = (
  dimension: 0 | 1,
  newSize: number,
  points: readonly Point[],
  normalize: boolean,
): Point[] => {
  const coordinates = points.map((point) => point[dimension]);
  const maxCoordinate = Math.max(...coordinates);
  const minCoordinate = Math.min(...coordinates);
  const size = maxCoordinate - minCoordinate;
  const scale = size === 0 ? 1 : newSize / size;

  let nextMinCoordinate = Infinity;

  const scaledPoints = points.map((point): Point => {
    const newCoordinate = point[dimension] * scale;
    const newPoint = [...point];
    newPoint[dimension] = newCoordinate;
    if (newCoordinate < nextMinCoordinate) {
      nextMinCoordinate = newCoordinate;
    }
    return newPoint as Point;
  });

  if (!normalize) {
    return scaledPoints;
  }

  if (scaledPoints.length === 2) {
    return scaledPoints;
  }

  const translation = minCoordinate - nextMinCoordinate;

  const nextPoints = scaledPoints.map((scaledPoint) =>
    point(
      ...(scaledPoint.map((value, currentDimension) => {
        return currentDimension === dimension ? value + translation : value;
      }) as [number, number]),
    ),
  );

  return nextPoints;
};
